<script setup>
import { computed, inject } from 'vue';
import { useI18n } from 'vue-i18n';
import SectionTitle from '@/components/common/section-title.vue';
import MaterialIcon from '@/components/common/MaterialIcon.vue';
import { AnimatedContent, ElectricBorder, SpotlightCard } from '@/components/vuebits';

const { t } = useI18n();
const openContact = inject('openContact', () => { });

const plans = computed(() => [
  {
    // 快速上线 · 多渠道客服与销售协同工作台
    icon: 'F',
    badge: t('plans.standardBadge'),
    title: t('plans.standardTitle'),
    desc: t('plans.standardDesc'),
    price: t('plans.standardPrice'),
    period: t('plans.standardPeriod'),
    features: [
      { icon: 'inbox', text: t('plans.standardFeature1') },
      { icon: 'translate', text: t('plans.standardFeature2') },
      { icon: 'support_agent', text: t('plans.standardFeature3') },
    ],
    highlight: true,
    label: 'POC·Flash',
  },
  {
    // 自选模型 · 专属 AI 技能
    icon: 'P',
    badge: null,
    title: t('plans.customTitle'),
    desc: t('plans.customDesc'),
    price: t('plans.customPrice'),
    period: t('plans.customPeriod'),
    features: [
      { icon: 'layers', text: t('plans.customFeature1') },
      { icon: 'auto_awesome', text: t('plans.customFeature2') },
      { icon: 'groups', text: t('plans.customFeature3') },
    ],
    highlight: false,
    label: 'POC·Pro',
  },
  {
    // 独立部署 · 数据隔离 · KA 专属服务
    icon: 'M',
    badge: t('plans.exclusiveBadge'),
    title: t('plans.exclusiveTitle'),
    desc: t('plans.exclusiveDesc'),
    price: t('plans.exclusivePrice'),
    period: t('plans.exclusivePeriod'),
    features: [
      { icon: 'dns', text: t('plans.exclusiveFeature1') },
      { icon: 'smart_toy', text: t('plans.exclusiveFeature2') },
      { icon: 'group_add', text: t('plans.exclusiveFeature3') },
    ],
    highlight: false,
    label: 'POC·Max',
  },
]);
</script>

<template>
  <section id="plans" class="section-space overflow-visible !pt-0 pb-16 md:!pb-30">
    <div class="section-shell">
      <AnimatedContent>
        <p class="eyebrow text-center">
          {{ t('plans.eyebrow') }}
        </p>
      </AnimatedContent>
      <SectionTitle :title="t('plans.title')" />
      <AnimatedContent :delay="0.05" :distance="24" class-name="-mt-7 mb-8 md:mb-12">
        <p class="mx-auto max-w-2xl text-center text-[15px] leading-7 text-muted-foreground md:text-base">
          {{ t('plans.subtitle') }}
        </p>
      </AnimatedContent>

      <div
        class="grid items-stretch gap-5 overflow-visible py-0 md:gap-8 lg:items-end lg:grid-cols-[minmax(0,1.16fr)_minmax(0,0.92fr)_minmax(0,0.92fr)] lg:gap-6 lg:pb-4 xl:gap-8"
      >
        <AnimatedContent
          v-for="(plan, index) in plans"
          :key="plan.title"
          :delay="index * 0.1"
          :distance="30"
          :class-name="plan.highlight
            ? 'h-full overflow-visible px-0 pt-6 pb-2 md:px-3 md:pt-10 md:pb-4 lg:-translate-y-8'
            : 'h-full lg:pt-10'
          "
        >
          <component
            :is="plan.highlight ? ElectricBorder : 'div'"
            v-bind="plan.highlight
              ? {
                color: '#155eef',
                borderRadius: 28,
                speed: 1.1,
                chaos: 0.1,
                className: 'relative h-full origin-center lg:scale-[1.06]',
              }
              : { class: 'h-full lg:scale-[0.98] lg:opacity-95' }
            "
          >
            <span
              v-if="plan.highlight"
              class="absolute -top-3 left-1/2 z-20 -translate-x-1/2 rounded-full bg-primary px-4 py-1 text-[10px] font-bold tracking-wider text-white shadow-lg shadow-primary/30 md:px-5 md:py-1.5 md:text-[11px]"
            >
              {{ t('plans.recommended') }}
            </span>
            <SpotlightCard
              :class-name="plan.highlight
                ? 'relative flex h-full min-h-0 flex-col !overflow-visible border-transparent bg-white p-5 md:min-h-[520px] md:p-9 lg:p-10'
                : 'relative flex h-full flex-col border-border bg-white p-5 md:p-8'
              "
              :spotlight-color="plan.highlight ? 'rgba(21, 94, 239, 0.18)' : 'rgba(21, 94, 239, 0.08)'"
            >
              <div class="flex items-start justify-between gap-3 md:gap-4">
                <span
                  class="grid shrink-0 place-items-center rounded-xl bg-primary/10 text-primary md:rounded-2xl"
                  :class="plan.highlight ? 'size-11 md:size-14' : 'size-10 md:size-12'"
                >
                  <span
                    class="font-bold uppercase leading-none"
                    :class="plan.highlight ? 'text-xl md:text-2xl' : 'text-lg md:text-xl'"
                  >
                    {{ plan.icon }}
                  </span>
                </span>
                <div class="min-w-0 flex-1">
                  <p
                    class="text-[11px] font-bold  tracking-wider text-primary md:text-xs"
                    :class="{ 'md:text-sm': plan.highlight }"
                  >
                    {{ plan.label }}
                  </p>
                  <h3
                    class="mt-1.5 font-bold text-foreground md:mt-2"
                    :class="plan.highlight ? 'text-xl md:text-2xl lg:text-[1.75rem]' : 'text-lg md:text-xl'"
                  >
                    {{ plan.title }}
                  </h3>
                </div>
                <span
                  v-if="plan.badge"
                  class="shrink-0 rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary md:px-2.5 md:text-xs"
                >
                  {{ plan.badge }}
                </span>
              </div>

              <p
                class="mt-3 leading-6 text-muted-foreground md:mt-4"
                :class="[
                  plan.highlight
                    ? 'min-h-0 text-sm md:min-h-14 md:text-base'
                    : 'min-h-0 text-sm md:min-h-12',
                ]"
              >
                {{ plan.desc }}
              </p>

              <p class="mt-5 border-y border-border py-4 md:mt-7 md:py-5">
                <span
                  class="font-bold tracking-tight text-foreground"
                  :class="plan.highlight ? 'text-3xl md:text-5xl' : 'text-3xl md:text-4xl'"
                >
                  {{ plan.price }}
                </span>
                <span class="ml-1 text-sm text-muted-foreground">{{ plan.period }}</span>
              </p>

              <ul
                class="mt-5 flex-1 space-y-2.5 text-sm text-muted-foreground md:mt-6 md:space-y-3"
                :class="plan.highlight ? 'md:space-y-3.5 md:text-base' : ''"
              >
                <li
                  v-for="feature in plan.features"
                  :key="feature.text"
                  class="flex gap-2.5 md:gap-3"
                >
                  <span
                    class="grid shrink-0 place-items-center rounded-full bg-primary/10 text-primary"
                    :class="plan.highlight ? 'size-5 md:size-6' : 'size-5'"
                  >
                    <MaterialIcon
                      :name="feature.icon"
                      :class="plan.highlight ? 'text-xs md:text-sm' : 'text-xs'"
                    />
                  </span>
                  {{ feature.text }}
                </li>
              </ul>

              <button
                type="button"
                class="mt-6 w-full rounded-lg font-bold transition hover:-translate-y-0.5 md:mt-8 md:rounded-xl"
                :class="plan.highlight
                  ? 'bg-primary py-3 text-sm text-primary-foreground shadow-lg shadow-primary/20 md:py-3.5 md:text-base'
                  : 'border border-border bg-background py-2.5 text-sm text-foreground hover:bg-muted md:py-3'
                "
                @click="openContact()"
              >
                {{ t('plans.cta') }}
              </button>
            </SpotlightCard>
          </component>
        </AnimatedContent>
      </div>
    </div>
  </section>
</template>
